import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import { InputTelefoneCustom } from '../lanz-ui';
import api, { normalize } from '../services/api';
import './EmpresaPage.css';

const ROLES = [
  { label: 'Admin', value: 'Admin' },
  { label: 'Gestor', value: 'Gestor' },
  { label: 'Usuário', value: 'User' },
];

const EMPTY = {
  id: null,
  nome: '',
  sobrenome: '',
  email: '',
  telefone: '',
  role: 'User',
  password: '',
  status: 'Ativo',
};

export default function UsuariosPanel() {
  const toast = useRef(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/usuarios');
      setUsuarios(normalize(data));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar usuários.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const setCampo = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const abrirNovo = () => {
    setForm(EMPTY);
    setDialogVisible(true);
  };

  const abrirEdicao = (row) => {
    setForm({
      id: row.id,
      nome: row.nome || '',
      sobrenome: row.sobrenome || '',
      email: row.email || '',
      telefone: row.telefone || '',
      role: row.role || 'User',
      password: '',
      status: row.status || 'Ativo',
    });
    setDialogVisible(true);
  };

  const salvar = async () => {
    if (!form.nome?.trim() || !form.email?.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Nome e e-mail são obrigatórios.' });
      return;
    }
    if (!form.id && !form.password) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe a senha do novo usuário.' });
      return;
    }

    setSalvando(true);
    try {
      if (form.id) {
        await api.put(`/usuarios/${form.id}`, {
          nome: form.nome,
          sobrenome: form.sobrenome,
          telefone: form.telefone,
          role: form.role,
          status: form.status,
          password: form.password || undefined,
        });
      } else {
        await api.post('/usuarios', {
          nome: form.nome,
          sobrenome: form.sobrenome,
          email: form.email,
          telefone: form.telefone,
          role: form.role,
          password: form.password,
        });
      }
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Usuário salvo.' });
      setDialogVisible(false);
      carregar();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Não foi possível salvar o usuário.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const desativar = (row) => {
    confirmDialog({
      message: `Desativar o usuário "${row.nome}"?`,
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: async () => {
        try {
          await api.delete(`/usuarios/${row.id}`);
          toast.current?.show({ severity: 'success', summary: 'Desativado', detail: 'Usuário desativado.' });
          carregar();
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Erro',
            detail: error.response?.data?.message || 'Não foi possível desativar.',
          });
        }
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        field: 'nome',
        header: 'Nome',
        body: (r) => `${r.nome || ''} ${r.sobrenome || ''}`.trim(),
        exportValue: (r) => `${r.nome || ''} ${r.sobrenome || ''}`.trim(),
      },
      { field: 'sobrenome', header: 'Sobrenome', defaultVisible: false },
      { field: 'email', header: 'E-mail' },
      { field: 'telefone', header: 'Telefone' },
      { field: 'role', header: 'Perfil' },
      {
        field: 'status',
        header: 'Status',
        body: (r) => <Tag severity={r.status === 'Ativo' ? 'success' : 'warning'} value={r.status} />,
        exportValue: (r) => r.status,
      },
      {
        field: '_acoes',
        header: 'Ações',
        sortable: false,
        alwaysVisible: true,
        body: (row) => (
          <div className="filiais-acoes">
            <Button icon="pi pi-pencil" rounded text onClick={() => abrirEdicao(row)} tooltip="Editar" />
            {row.status === 'Ativo' && (
              <Button icon="pi pi-ban" rounded text severity="danger" onClick={() => desativar(row)} tooltip="Desativar" />
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="usuarios-panel">
      <Toast ref={toast} />
      <ConfirmDialog acceptLabel="Sim" rejectLabel="Não" />

      <div className="filiais-header" style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, color: '#002478', fontSize: '1.2rem' }}>Usuários da empresa</h2>
        <Button label="Novo usuário" icon="pi pi-plus" onClick={abrirNovo} />
      </div>

      <CadastroDataTable
        value={usuarios}
        columns={columns}
        storageKey="usuarios"
        loading={loading}
        title="Usuários"
        emptyMessage="Nenhum usuário cadastrado."
        globalFilterFields={['nome', 'sobrenome', 'email', 'telefone', 'role', 'status']}
      />

      <Dialog
        header={form.id ? 'Editar usuário' : 'Novo usuário'}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '560px' }}
        breakpoints={{ '640px': '95vw' }}
      >
        <div className="agentes-form" style={{ boxShadow: 'none', padding: '8px 4px' }}>
          <div className="nfse-form-row">
            <div className="agentes-field">
              <label>Nome</label>
              <InputText value={form.nome} onChange={(e) => setCampo('nome', e.target.value)} />
            </div>
            <div className="agentes-field">
              <label>Sobrenome</label>
              <InputText value={form.sobrenome} onChange={(e) => setCampo('sobrenome', e.target.value)} />
            </div>
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field">
              <label>E-mail</label>
              <InputText
                value={form.email}
                onChange={(e) => setCampo('email', e.target.value)}
                disabled={!!form.id}
              />
            </div>
            <div className="agentes-field">
              <label>Telefone</label>
              <InputTelefoneCustom value={form.telefone} onChange={(v) => setCampo('telefone', v)} />
            </div>
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field">
              <label>Perfil</label>
              <Dropdown value={form.role} options={ROLES} onChange={(e) => setCampo('role', e.value)} />
            </div>
            {form.id && (
              <div className="agentes-field">
                <label>Status</label>
                <Dropdown
                  value={form.status}
                  options={[
                    { label: 'Ativo', value: 'Ativo' },
                    { label: 'Inativo', value: 'Inativo' },
                  ]}
                  onChange={(e) => setCampo('status', e.value)}
                />
              </div>
            )}
          </div>
          <div className="nfse-form-row">
            <div className="agentes-field">
              <label>{form.id ? 'Nova senha (opcional)' : 'Senha'}</label>
              <Password
                value={form.password}
                onChange={(e) => setCampo('password', e.target.value)}
                feedback={false}
                toggleMask
                inputStyle={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="agentes-dialog-footer">
          <Button label="Cancelar" className="p-button-text" onClick={() => setDialogVisible(false)} />
          <Button label="Salvar" icon="pi pi-save" loading={salvando} onClick={salvar} />
        </div>
      </Dialog>
    </div>
  );
}
